import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, ChevronRight, Fuel, XCircle } from "lucide-react";
import { useToast } from "../../Moduels/Context/ToastContext";
import {
  apiMessage,
  approveFuelBill,
  duplicateFromError,
  getFuelBills,
  rejectFuelBill,
} from "../../Moduels/AllowancesBenefits/allowancesApi";
import { formatDate, formatMoney } from "../../Moduels/AllowancesBenefits/shared";
import { orgScope } from "../../services/orgScope";
import "../../styles/employee-fuel.css";

export function fuelReviewQueryKey() {
  return ["fuel-pending-review", orgScope()];
}
const POLL_MS = 8000;

export function usePendingFuelReview({ enabled = true } = {}) {
  return useQuery({
    queryKey: fuelReviewQueryKey(),
    queryFn: () => getFuelBills({ status: "PENDING", limit: 8, offset: 0 }),
    enabled,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    staleTime: 4000,
    refetchOnWindowFocus: false,
  });
}

export default function FuelBillReviewCard({ query }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busyId, setBusyId] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [reason, setReason] = useState("");
  const [duplicateId, setDuplicateId] = useState(null);

  const data = query?.data;
  if (query?.isError && query.error?.response?.status === 404) return null;
  if (data && data.enabled === false) return null;
  if (!query?.isLoading && !data && query?.isError) {
    return (
      <section className="dash-card" id="dash-fuel-review">
        <div className="dash-card-head">
          <h2><span className="dash-head-icon warn" aria-hidden><Fuel size={15} /></span>Fuel bill verification</h2>
        </div>
        <p className="dash-empty">{apiMessage(query.error, "Unable to load fuel requests.")}</p>
        <button type="button" className="dash-link" onClick={() => query.refetch()}>Retry</button>
      </section>
    );
  }

  const bills = data?.bills || [];
  const total = data?.total || 0;

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ["fuel-pending-review"] });
  }

  async function onApprove(billId, allowDuplicate = false) {
    setBusyId(billId);
    try {
      await approveFuelBill(billId, { allowDuplicate });
      setDuplicateId(null);
      toast.success("Fuel bill approved. It will be added to the employee salary for that bill month.");
      await refresh();
    } catch (err) {
      if (duplicateFromError(err)) {
        setDuplicateId(billId);
        return;
      }
      toast.error(apiMessage(err, "Unable to approve this fuel bill."));
    } finally {
      setBusyId(null);
    }
  }

  async function onReject(billId) {
    const text = reason.trim();
    if (!text) {
      toast.error("Enter the reason for rejection. The employee will see it.");
      return;
    }
    setBusyId(billId);
    try {
      await rejectFuelBill(billId, text);
      setRejectId(null);
      setReason("");
      toast.success("Fuel bill rejected. The employee can see the reason.");
      await refresh();
    } catch (err) {
      toast.error(apiMessage(err, "Unable to reject this fuel bill."));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="dash-card" id="dash-fuel-review" aria-labelledby="dash-fuel-title">
      <div className="dash-card-head">
        <div>
          <h2 id="dash-fuel-title">
            <span className="dash-head-icon warn" aria-hidden><Fuel size={15} /></span>
            Fuel bill verification
          </h2>
          <p className="dash-card-sub">
            Employee requests. Check the bill, bill number, and date, then approve or reject.
          </p>
        </div>
        <span className={`fuel-live${query?.isFetching ? " is-on" : ""}`}>
          {query?.isFetching ? "Updating" : "Live"}
        </span>
      </div>

      {query?.isLoading && !data ? (
        <p className="dash-empty">Loading fuel requests…</p>
      ) : bills.length === 0 ? (
        <p className="dash-empty">No fuel bills are waiting for verification.</p>
      ) : (
        <ul className="fuel-review-list">
          {bills.map((bill) => (
            <li key={bill.id} className="fuel-review-item">
              <div className="fuel-review-main">
                <strong>{bill.employee_name || "Employee"}</strong>
                <span>
                  {bill.employee_code ? `${bill.employee_code} · ` : ""}
                  {bill.department_name || "No department"}
                </span>
                <dl>
                  <div>
                    <dt>Bill number</dt>
                    <dd>{bill.bill_number || "—"}</dd>
                  </div>
                  <div>
                    <dt>Bill date</dt>
                    <dd>{formatDate(bill.bill_date)}</dd>
                  </div>
                  <div>
                    <dt>Amount</dt>
                    <dd>{formatMoney(bill.amount)}</dd>
                  </div>
                </dl>
                {bill.remarks ? <p>Employee note: {bill.remarks}</p> : null}
              </div>
              <div className="fuel-review-actions">
                {duplicateId === bill.id ? (
                  <p className="fuel-review-warn">
                    An approved bill already exists on this date.
                    <button type="button" disabled={busyId === bill.id} onClick={() => onApprove(bill.id, true)}>
                      Approve anyway
                    </button>
                  </p>
                ) : (
                  <button
                    type="button"
                    className="fuel-review-approve"
                    disabled={busyId === bill.id}
                    onClick={() => onApprove(bill.id)}
                  >
                    <CheckCircle2 size={15} />
                    {busyId === bill.id && rejectId !== bill.id ? "Approving…" : "Approve"}
                  </button>
                )}
                {rejectId === bill.id ? (
                  <div className="fuel-review-rejectbox">
                    <label>
                      Rejection reason
                      <textarea
                        rows={3}
                        value={reason}
                        maxLength={500}
                        placeholder="Explain why this bill is rejected"
                        onChange={(event) => setReason(event.target.value)}
                      />
                    </label>
                    <div>
                      <button
                        type="button"
                        className="fuel-review-reject"
                        disabled={busyId === bill.id}
                        onClick={() => onReject(bill.id)}
                      >
                        {busyId === bill.id ? "Rejecting…" : "Confirm reject"}
                      </button>
                      <button type="button" onClick={() => { setRejectId(null); setReason(""); }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="fuel-review-reject"
                    disabled={busyId === bill.id}
                    onClick={() => {
                      setRejectId(bill.id);
                      setReason("");
                      setDuplicateId(null);
                    }}
                  >
                    <XCircle size={15} /> Reject
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        className="dash-link dash-card-foot"
        onClick={() => navigate("/settings/allowances-benefits/fuel-reimbursement")}
      >
        {total > bills.length ? `View all ${total} pending bills` : "Open fuel reimbursement"} <ChevronRight size={14} />
      </button>
    </section>
  );
}
